/* eslint-disable react/prop-types */
import React, { useCallback, useRef } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { values } from 'rambdax';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import CurrencyInput from '@/components/common/form/CurrencyInput';
import DateInput from '@/components/common/form/DateInput';
import TextSelectField from '@/components/common/form/TextSelectField';
import Loader from '@/components/common/Loader';
import CampaignGoalType from '@/constants/campaigns/CampaignGoalType';
import NumberFormatReturnedValues from '@/constants/ui/NumberFormatReturnedValues';
import useMobileView from '@/hooks/ui/useMobileView';
import { CAMPAIGN_GOALS } from '@/lib/configs/campaigns';
import { API_DATE_FORMAT } from '@/lib/constants/common';
import translate from '@/lib/intl/translate';
import { TextSelectFieldValueType } from '@/propTypes';
import CampaignType from '@/propTypes/CampaignType';
import { formatCurrency, formatCurrencyInputToCents } from '@/utils/currency';
import { formatDateObject, parseDateValue } from '@/utils/dates';

const CLASSES = {
  textSelectField: {
    formControl: 'col-span-full md:col-span-6',
  },
  dateInput: {
    root: 'col-span-full md:col-span-6',
  },
  fullWidth: {
    formControl: 'col-span-full',
  },
};
function CampaignModal({
  addFundraiser,
  addFundraiserLoading,
  hasPledgeIntegration,
  data,
  teamMembers,
  errors,
  handleClose,
  handleSave,
  hasApiError,
  isCreating,
  isLoading,
  lockedFields,
  open,
  updateData,
  updateErrors,
}) {
  const isMobile = useMobileView();
  const modalRef = useRef();

  const {
    assignees,
    description,
    endDate,
    goalAmount,
    goalType,
    itemQuantity,
    itemType,
    itemValue,
    name,
    primaryAssigneeId,
    startDate,
    stretchAmount,
    url,
  } = data;

  const onTextFieldChange = useCallback(
    (fieldName, hasEvent = true, format) =>
      (res) => {
        const newValue = hasEvent ? res?.target?.value : res;

        updateData({
          ...data,
          [fieldName]: format ? format(newValue) : newValue,
        });

        updateErrors({
          ...errors,
          [fieldName]: false,
        });
      },
    [data, errors],
  );

  const onSelect = useCallback(
    (fieldName, useValue = true) =>
      (selectedOption) => {
        const newValue = useValue ? selectedOption?.value : selectedOption;

        updateData({
          ...data,
          [fieldName]: newValue,
        });

        updateErrors({
          ...errors,
          [fieldName]: false,
        });
      },
    [data, errors],
  );

  const onDateChange = useCallback(
    (fieldName) => (newDate) => {
      updateData({
        ...data,
        [fieldName]: formatDateObject(newDate, API_DATE_FORMAT),
      });
    },
    [data],
  );

  const onPrimaryAdministratorChange = useCallback(
    (value) => {
      let updatedAdministrators = assignees;

      // makes sure to select an administrator if it's primary
      if (value && !assignees.find((admin) => admin.value === value)) {
        updatedAdministrators = [
          ...assignees,
          teamMembers.find((admin) => admin.value === value),
        ];
      }

      updateData({
        ...data,
        primaryAssigneeId: value,
        assignees: updatedAdministrators,
      });
    },
    [assignees, teamMembers],
  );

  const isDisabled = (fieldName) => lockedFields.includes(fieldName);

  return (
    <Dialog
      data-testid="campaign-modal"
      fullWidth
      maxWidth="md"
      mobile={Number(isMobile)}
      onClose={handleClose}
      open={open}
      ref={modalRef}
    >
      <DialogHeader
        headerText={translate(
          isCreating ? 'CREATE_NEW_CAMPAIGN_TITLE' : 'EDIT_CAMPAIGN_TITLE',
        )}
        onClose={handleClose}
      />
      <DialogContent dividers>
        <div className="grid grid-cols-12 gap-3">
          <TextField
            className="col-span-full"
            disabled={isDisabled('name')}
            error={errors?.name}
            helperText={
              errors?.name ? translate('CAMPAIGN_NAME_REQUIRED_LABEL') : ''
            }
            inputProps={{ 'data-testid': 'campaign-name-input' }}
            label={translate('CAMPAIGN_NAME_LABEL')}
            onChange={onTextFieldChange('name')}
            required
            value={name}
          />
          <TextField
            className="col-span-full"
            disabled={isDisabled('description')}
            inputProps={{ 'data-testid': 'campaign-description-input' }}
            label={translate('CAMPAIGN_DESCRIPTION_LABEL')}
            onChange={onTextFieldChange('description')}
            value={description}
          />
          <TextField
            className="col-span-full"
            disabled={isDisabled('url')}
            inputProps={{ 'data-testid': 'campaign-url-input' }}
            label={translate('CAMPAIGN_WEBSITE_LABEL')}
            onChange={onTextFieldChange('url')}
            type="url"
            value={url}
          />
          <TextSelectField
            classes={CLASSES.fullWidth}
            disabled={isDisabled('goalType')}
            disableClearable
            id="campaign-goal-selector"
            items={CAMPAIGN_GOALS}
            label={translate('CAMPAIGN_GOAL_LABEL')}
            setValue={onSelect('goalType')}
            value={CAMPAIGN_GOALS.find((item) => item.value === goalType)}
          />
          <CurrencyInput
            amount={formatCurrency(goalAmount, true)}
            className="col-span-6"
            disabled={isDisabled('goals')}
            error={errors?.goalAmount}
            errorHelperText={translate('CAMPAIGN_GOAL_AMOUNT_REQUIRED_LABEL')}
            label={translate('CAMPAIGN_GOAL_AMOUNT_LABEL')}
            required
            returnedValue={NumberFormatReturnedValues.FLOAT_VALUE}
            setAmount={onTextFieldChange(
              'goalAmount',
              false,
              formatCurrencyInputToCents,
            )}
          />
          <CurrencyInput
            amount={formatCurrency(stretchAmount, true)}
            className="col-span-6"
            error={errors?.stretchAmount}
            errorHelperText={translate('CAMPAIGN_STRETCH_GOAL_REQUIRED_LABEL')}
            label={translate('CAMPAIGN_STRETCH_GOAL_LABEL')}
            returnedValue={NumberFormatReturnedValues.FLOAT_VALUE}
            setAmount={onTextFieldChange(
              'stretchAmount',
              false,
              formatCurrencyInputToCents,
            )}
            disabled={isDisabled('stretchAmount')}
          />
          {goalType === CampaignGoalType.IN_KIND ? (
            <>
              <TextField
                className="col-span-full"
                error={errors?.itemType}
                helperText={
                  errors?.itemType
                    ? translate('CAMPAIGN_ITEM_TYPE_REQUIRED_LABEL')
                    : ''
                }
                fullWidth
                inputProps={{ 'data-testid': 'campaign-item-type-input' }}
                label={translate('CAMPAIGN_ITEM_TYPE_LABEL')}
                onChange={onTextFieldChange('itemType')}
                required
                value={itemType}
                disabled={isDisabled('itemType')}
              />
              <TextField
                className="col-span-full md:col-span-6"
                error={errors?.itemQuantity}
                helperText={
                  errors?.itemQuantity
                    ? translate('CAMPAIGN_NUMBER_OF_ITEMS_REQUIRED_LABEL')
                    : ''
                }
                fullWidth
                inputProps={{ 'data-testid': 'campaign-item-quantity-input' }}
                label={translate('CAMPAIGN_NUMBER_OF_ITEMS_LABEL')}
                min={0}
                onChange={onTextFieldChange('itemQuantity', true, parseInt)}
                required
                value={itemQuantity}
                type="number"
                disabled={isDisabled('itemQuantity')}
              />
              <CurrencyInput
                amount={itemValue}
                className="col-span-full md:col-span-6"
                error={errors?.itemValue}
                errorHelperText={translate(
                  'CAMPAIGN_ESTIMATED_ITEM_VALUE_REQUIRED_LABEL',
                )}
                label={translate(
                  isMobile
                    ? 'CAMPAIGN_ESTIMATED_VALUE_LABEL'
                    : 'CAMPAIGN_ESTIMATED_ITEM_VALUE_LABEL',
                )}
                required
                returnedValue={NumberFormatReturnedValues.FLOAT_VALUE}
                setAmount={onTextFieldChange('itemValue', false)}
                disabled={isDisabled('itemValue')}
              />
            </>
          ) : null}
          <DateInput
            classes={CLASSES.dateInput}
            disabled={isDisabled('startDate')}
            id="campaign-date-picker-start-date"
            label={translate('CAMPAIGN_START_DATE_LABEL')}
            onChange={onDateChange('startDate')}
            value={parseDateValue(startDate)}
          />
          <DateInput
            classes={CLASSES.dateInput}
            disabled={isDisabled('endDate')}
            id="campaign-date-picker-end-date"
            label={translate('CAMPAIGN_END_DATE_LABEL')}
            onChange={onDateChange('endDate')}
            value={endDate && parseDateValue(endDate)}
          />
          <div className="col-span-full">
            <TextSelectField
              id="campaign-assignees-selector"
              error={errors?.assignees}
              items={teamMembers}
              multiple
              value={assignees}
              setValue={onSelect('assignees', false)}
              label={translate('CAMPAIGN_ADMINISTRATORS_LABEL')}
              primaryValue={primaryAssigneeId}
              setPrimaryValue={onPrimaryAdministratorChange}
              usePrimary
              disabled={isDisabled('assignees')}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {Boolean(values(errors).find((error) => error) || hasApiError) && (
              <div className="px-2">
                <Typography data-testid="campaign-modal-error" color="error">
                  {translate(
                    hasApiError
                      ? 'API_ERROR_UNKNOWN'
                      : 'CAMPAIGN_FORM_FIX_ERRORS_MESSAGE',
                  )}
                </Typography>
              </div>
            )}
            {hasPledgeIntegration && (
              <Button
                type="button"
                variant="contained"
                color="secondary"
                className="order-1 mb-4 w-full sm:order-2 sm:mb-0 sm:mr-6 sm:w-auto"
                classes={{ label: 'py-2.5 px-4' }}
                onClick={addFundraiser}
              >
                {addFundraiserLoading
                  ? translate('LOADING')
                  : translate('INTEGRATIONS_PLEDGE_ADD_FUNDRAISER')}
              </Button>
            )}
            <DialogActionButton
              className="order-2 mb-4 w-full sm:order-1 sm:mb-0 sm:w-auto"
              data-testid="campaign-modal-save-button"
              label={translate('SAVE')}
              onClick={handleSave}
            />
            <DialogCancelButton
              className="order-3 w-full sm:w-auto sm:pr-6"
              data-testid="campaign-modal-cancel-button"
              label={translate('CANCEL')}
              onClick={handleClose}
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

CampaignModal.propTypes = {
  addFundraiser: PropTypes.func,
  addFundraiserLoading: PropTypes.bool,
  hasPledgeIntegration: PropTypes.bool,
  data: PropTypes.shape(CampaignType).isRequired,
  teamMembers: PropTypes.arrayOf(TextSelectFieldValueType),
  errors: PropTypes.shape({}),
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  hasApiError: PropTypes.bool,
  isCreating: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  lockedFields: PropTypes.arrayOf(PropTypes.string),
  open: PropTypes.bool.isRequired,
  updateData: PropTypes.func.isRequired,
  updateErrors: PropTypes.func.isRequired,
};

CampaignModal.defaultProps = {
  addFundraiser: () => {},
  addFundraiserLoading: false,
  hasPledgeIntegration: false,
  teamMembers: [],
  errors: {},
  hasApiError: false,
  lockedFields: [],
  isLoading: false,
};

export default CampaignModal;
